import React, { useEffect, useState, useRef } from "react";
import Header from "../../../Componets/Header";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { Button } from "antd";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { registerCairoFont } from "../cairo-font";
import ScrollWrapper from "../../../Componets/ScrollWrapper";
import ReactToPrint from "react-to-print";
import Loadercomponent from "../../../Componets/Loader";
import { get_consolidated_sales_report } from "../../../Controllers/Dashboard/Dashboard";
import moment from "moment";

const ConsolidatedSalesReport = () => {
    const location = useLocation();
    const componentRef = useRef();

    const [loader, setLoader] = useState(false);
    const [body, setBody] = useState(null);
    const [role, setRole] = useState("");

    const headers = {
        name: "Consolidated Sales Report",
        components: [
            {
                type: "filter",
                name: "branch",
                className: "w-48",
                allowClear: true,
                showSearch: true,
                filterOption: false,
                placeholder: "Branch",
                api: "get-all-branches",
                value: null,
                options: null,
            },
            {
                type: "range",
                name: "date",
                value: {
                    start: "",
                    end: "",
                },
            },
        ],
        navigate: {
            tabs: [
                { name: "Reports", path: "/app/reports" },
                { name: "Consolidated Sales", path: "" },
            ],
            total: [],
        },
    };

    const [header, setHeader] = useState(headers);

    const formdata = (data) => fetchData(data);

    const fetchData = async (tabledata) => {
        try {
            setLoader(true);
            const data = {
                branch: tabledata?.branch,
                date: tabledata?.date,
            };
            const result = await get_consolidated_sales_report(data);
            if (result?.status) {
                setBody(result?.data);
                setRole(result?.data?.role);
            } else {
                toast.error(result?.message);
            }
        } catch (error) {
            toast.error(error?.message);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (!role) return;
        let updatedHeader = { ...headers };
        if (role !== "SUPERADMIN") {
            updatedHeader.components = updatedHeader.components.filter(
                (item) => item.name !== "branch"
            );
        }
        setHeader(updatedHeader);
    }, [role]);

    const formatDate = (dateString) => {
        return moment(dateString).format("DD/MM/YYYY");
    };

    // Footer totals helper
    const sumField = (field) =>
        (body?.reports || [])
            .reduce((acc, row) => acc + (parseFloat(row[field]) || 0), 0)
            .toFixed(3);

    const downloadCSV = () => {
        if (!body?.reports?.length) {
            toast.error("No data available to download");
            return;
        }

        try {
            const headerRow = [
                "Date",
                "Branch",
                "Invoices",
                "Returns",
                "Cash",
                "Card",
                "Bank Transfer",
                "Online",
                "Cheque",
                "Returned Amt",
                "Paid",
                "Payable",
                "Subtotal",
                "Discount",
                "Tax [VAT]",
                "Total",
            ];

            const rows = body.reports.map((row) => [
                formatDate(row.date),
                row.branch?.name,
                row.invoice_count,
                row.return_count || 0,
                row.total_cash.toFixed(3),
                (row.total_debit_card + row.total_credit_card).toFixed(3),
                row.total_bank_transfer.toFixed(3),
                row.total_online_payment.toFixed(3),
                row.total_cheque.toFixed(3),
                row.total_returns?.toFixed(3) || "0.000",
                row.total_paid.toFixed(3),
                row.total_payable.toFixed(3),
                row.subtotal.toFixed(3),
                row.discount.toFixed(3),
                row.taxamount.toFixed(3),
                row.total.toFixed(3),
            ]);

            // Totals row at bottom
            const totalsRow = [
                "TOTAL",
                "",
                body.reports.reduce((a, r) => a + r.invoice_count, 0),
                body.reports.reduce((a, r) => a + (r.return_count || 0), 0),
                sumField("total_cash"),
                body.reports
                    .reduce(
                        (a, r) =>
                            a +
                            (parseFloat(r.total_debit_card) || 0) +
                            (parseFloat(r.total_credit_card) || 0),
                        0
                    )
                    .toFixed(3),
                sumField("total_bank_transfer"),
                sumField("total_online_payment"),
                sumField("total_cheque"),
                sumField("total_returns"),
                sumField("total_paid"),
                sumField("total_payable"),
                sumField("subtotal"),
                sumField("discount"),
                sumField("taxamount"),
                sumField("total"),
            ];

            const csvContent = [headerRow, ...rows, totalsRow]
                .map((row) => row.map((field) => `"${field}"`).join(","))
                .join("\n");

            const encoded = new TextEncoder().encode("\uFEFF" + csvContent);
            const blob = new Blob([encoded], { type: "text/csv;charset=utf-8" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `Consolidated_Sales_Report_${new Date()
                .toLocaleDateString()
                .replace(/\//g, "_")}.csv`;
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (error) {
            toast.error("Failed to generate CSV file");
        }
    };

    const downloadPDF = () => {
        if (!body?.reports?.length) return;

        const doc = new jsPDF({
            orientation: "landscape",
            unit: "mm",
            format: "a4",
        });

        registerCairoFont(doc);
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFont("Cairo-Bold", "bold");
        doc.setFontSize(18);
        doc.text("Consolidated Sales Report", pageWidth / 2, 18, {
            align: "center",
        });

        const now = new Date();
        doc.setFontSize(10);
        doc.text(
            `Downloaded on: ${now.toLocaleDateString("en-GB")} ${now.toLocaleTimeString()}`,
            pageWidth / 2,
            26,
            { align: "center" }
        );

        const tableRows = body.reports.map((row) => [
            formatDate(row.date),
            row.branch?.name,
            row.invoice_count,
            row.return_count || 0,
            row.total_cash.toFixed(3),
            (row.total_debit_card + row.total_credit_card).toFixed(3),
            row.total_bank_transfer.toFixed(3),
            row.total_online_payment.toFixed(3),
            row.total_cheque.toFixed(3),
            row.total_returns?.toFixed(3) || "0.000",
            row.total_paid.toFixed(3),
            row.total_payable.toFixed(3),
            row.subtotal.toFixed(3),
            row.discount.toFixed(3),
            row.taxamount.toFixed(3),
            row.total.toFixed(3),
        ]);

        // Totals row
        tableRows.push([
            "TOTAL",
            body.reports.reduce((a, r) => a + r.invoice_count, 0),
            body.reports.reduce((a, r) => a + (r.return_count || 0), 0),
            sumField("total_cash"),
            body.reports
                .reduce(
                    (a, r) =>
                        a +
                        (parseFloat(r.total_debit_card) || 0) +
                        (parseFloat(r.total_credit_card) || 0),
                    0
                )
                .toFixed(3),
            sumField("total_bank_transfer"),
            sumField("total_online_payment"),
            sumField("total_cheque"),
            sumField("total_returns"),
            sumField("total_paid"),
            sumField("total_payable"),
            sumField("subtotal"),
            sumField("discount"),
            sumField("taxamount"),
            sumField("total"),
        ]);

        autoTable(doc, {
            startY: 32,
            head: [[
                "Date", "Branch", "Invoices", "Cash", "Card",
                "Bank Transfer", "Online", "Cheque", "Returned Amt",
                "Paid", "Payable", "Subtotal", "Discount", "Tax", "Total",
            ]],
            body: tableRows,
            foot: [],
            didParseCell: (data) => {
                // Bold the totals row
                if (data.row.index === tableRows.length - 1) {
                    data.cell.styles.fontStyle = "bold";
                }
            },
            styles: {
                font: "Cairo",
                textColor: [0, 0, 0],
                lineColor: [7, 181, 83],
                lineWidth: 0.2,
            },
            headStyles: {
                font: "Cairo",
                fontStyle: "bold",
                fillColor: false,
                lineColor: [7, 181, 83],
                lineWidth: 0.6,
            },
        });

        doc.save(
            `Consolidated_Sales_Report_${now.toLocaleDateString("en-GB").replace(/\//g, "_")}.pdf`
        );
    };

    return (
        <Header headers={header} setHeaders={setHeader} formdata={formdata}>
            <Loadercomponent loader={loader}>
                <div className="text-right flex gap-3 justify-end mt-4">
                    <Button
                        className="!bg-[#07b553] !border-[#07b553] hover:!bg-[#38cb82] hover:!border-[#38cb82]"
                        type="primary"
                        onClick={downloadCSV}
                    >
                        Download CSV
                    </Button>

                    <ReactToPrint
                        trigger={() => (
                            <Button
                                className="!bg-[#07b553] !border-[#07b553] hover:!bg-[#38cb82] hover:!border-[#38cb82]"
                                type="primary"
                            >
                                Download PDF
                            </Button>
                        )}
                        content={() => componentRef.current}
                        documentTitle={`Consolidated_Sales_Report_${new Date()
                            .toLocaleDateString("en-GB")
                            .replace(/\//g, "_")}`}
                        pageStyle={`
              @page {
                size: landscape;
                margin: 10mm;
              }
              @media print {
                thead { display: table-header-group !important; }
                tfoot { display: table-row-group !important; position: static !important; bottom: auto !important; }
                table, thead, tbody, tfoot, tr, th, td {
                  border: 1px solid #000 !important;
                  padding: 2px !important;
                  font-size: 10px !important;
                }
                th {
                  background-color: #f4f4f4 !important;
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                table {
                  width: 100% !important;
                  border-collapse: collapse !important;
                }
              }
            `}
                    />
                </div>

                <ScrollWrapper>
                    <div
                        className="max-h-[70vh] overflow-y-auto scrollbar-hide remove-scroll-limit"
                        ref={componentRef}
                    >
                        <table className="w-full bg-white">
                            <thead className="text-gray-500 sticky top-0 bg-white z-10">
                                <tr>
                                    <td className="border p-4">Date</td>
                                    <td className="border p-4">Branch</td>
                                    <td className="border p-4">Invoices</td>
                                    <td className="border p-4">Returns</td>
                                    <td className="border p-4">Cash</td>
                                    <td className="border p-4">Card</td>
                                    <td className="border p-4">Bank Transfer</td>
                                    <td className="border p-4">Online</td>
                                    <td className="border p-4">Cheque</td>
                                    <td className="border p-4">Returned Amt</td>
                                    <td className="border p-4">Paid</td>
                                    <td className="border p-4">Payable</td>
                                    <td className="border p-4">Net Subtotal</td>
                                    <td className="border p-4">Discount</td>
                                    <td className="border p-4">Tax&nbsp;[VAT]</td>
                                    <td className="border p-4"> Net Total</td>
                                </tr>
                            </thead>
                            <tbody>
                                {body?.reports?.length > 0 ? (
                                    body.reports.map((row, index) => (
                                        <tr key={index}>
                                            <td className="border p-4">{formatDate(row.date)}</td>
                                            <td className="border p-4">{row.branch?.name || "-"}</td>
                                            <td className="border p-4">{row.invoice_count}</td>
                                            <td className="border p-4">{row.return_count || 0}</td>
                                            <td className="border p-4">{row.total_cash.toFixed(3)}</td>
                                            <td className="border p-4">
                                                {(row.total_debit_card + row.total_credit_card).toFixed(3)}
                                            </td>
                                            <td className="border p-4">{row.total_bank_transfer.toFixed(3)}</td>
                                            <td className="border p-4">{row.total_online_payment.toFixed(3)}</td>
                                            <td className="border p-4">{row.total_cheque.toFixed(3)}</td>
                                            <td className="border p-4">{row.total_returns?.toFixed(3) || "0.000"}</td>
                                            <td className="border p-4">{row.total_paid.toFixed(3)}</td>
                                            <td className="border p-4">{row.total_payable.toFixed(3)}</td>
                                            <td className="border p-4">{row.subtotal.toFixed(3)}</td>
                                            <td className="border p-4">{row.discount.toFixed(3)}</td>
                                            <td className="border p-4">{row.taxamount.toFixed(3)}</td>
                                            <td className="border p-4">{row.total.toFixed(3)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={15} className="border p-4 text-center text-gray-400">
                                            No data found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            <tfoot className="sticky bottom-0 bg-white z-10">
                                <tr className="text-sm font-semibold">
                                    <td className="border p-4">Total</td>
                                    <td className="border p-4"></td>
                                    <td className="border p-4">
                                        {body?.reports?.reduce((a, r) => a + r.invoice_count, 0) || 0}
                                    </td>
                                    <td className="border p-4">
                                        {body?.reports?.reduce((a, r) => a + (r.return_count || 0), 0) || 0}
                                    </td>
                                    <td className="border p-4">{sumField("total_cash")}</td>
                                    <td className="border p-4">
                                        {(body?.reports || [])
                                            .reduce(
                                                (a, r) =>
                                                    a +
                                                    (parseFloat(r.total_debit_card) || 0) +
                                                    (parseFloat(r.total_credit_card) || 0),
                                                0
                                            )
                                            .toFixed(3)}
                                    </td>
                                    <td className="border p-4">{sumField("total_bank_transfer")}</td>
                                    <td className="border p-4">{sumField("total_online_payment")}</td>
                                    <td className="border p-4">{sumField("total_cheque")}</td>
                                    <td className="border p-4">{sumField("total_returns")}</td>
                                    <td className="border p-4">{sumField("total_paid")}</td>
                                    <td className="border p-4">{sumField("total_payable")}</td>
                                    <td className="border p-4">{sumField("subtotal")}</td>
                                    <td className="border p-4">{sumField("discount")}</td>
                                    <td className="border p-4">{sumField("taxamount")}</td>
                                    <td className="border p-4">{sumField("total")}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </ScrollWrapper>
            </Loadercomponent>
        </Header>
    );
};

export default ConsolidatedSalesReport;